import { useCallback, useLayoutEffect, useRef, type CSSProperties, type ReactNode, type RefObject, type WheelEvent as ReactWheelEvent } from 'react';
import { cn } from '@core/ui/cn';
import { GridCorner, GridHeader } from './GridHeader';
import type { Grid } from './geometry';
import type { ContextBand } from './ContextBand';
import { useScrollMemory } from '@core/ui/scrollMemory';

export type SplitRow = {
  key: string;
  height: number;
  className?: string;        // общее для обеих ячеек: рамки, прозрачность
  style?: CSSProperties;     // общее для обеих ячеек: заливка цветом дорожки (лонч)
  label: ReactNode;
  labelClassName?: string;
  grid: ReactNode;
  gridClassName?: string;
  gridAttrs?: Record<string, string>;
  group?: string;            // соседние строки одной группы — один блок: липкая строка держится, пока блок на экране
  sticky?: boolean;          // прилипает к верху тела при прокрутке (заголовок проекта без задач)
};

type Props = {
  gridRef: RefObject<HTMLDivElement | null>;
  grid: Grid;
  labelWidth: number;
  cornerLabel: string;
  rows: SplitRow[];
  cursor?: CSSProperties['cursor'];
  context?: Pick<ContextBand, 'height' | 'node' | 'corner'>;   // полоса контекста под шапкой: события, отпуска, пины
  header?: ReactNode;        // своя шапка вместо недельной (лонч: дни)
  overlay?: ReactNode;       // слой поверх всех строк тела (стрелки связей): absolute, без событий мыши
  scrollKey?: string;        // память прокрутки на время сессии (useScrollMemory)
};

// Сетка из четырёх частей: угол, шапка, колонка названий и тело. Прокручивается только тело,
// шапка едет за ним через transform, колонка названий — через scrollTop (overflow: hidden всё
// равно прокручиваемый контейнер, и в нём работает position: sticky) — так полосы прокрутки
// живут на теле, а не поверх закреплённых частей.
export function SplitGrid({ gridRef, grid, labelWidth, cornerLabel, rows, cursor, context, header, overlay, scrollKey }: Props) {
  useScrollMemory(gridRef, scrollKey ?? null);
  const headerInner = useRef<HTMLDivElement>(null);
  const labelsCol = useRef<HTMLDivElement>(null);
  const labelsInner = useRef<HTMLDivElement>(null);
  const labelsSet = useRef(0); // что мы сами поставили колонке: её scroll-событие с этим значением — эхо, не чужая прокрутка
  const sync = useCallback(() => {
    const g = gridRef.current;
    if (!g) return;
    if (headerInner.current) headerInner.current.style.transform = `translateX(${-g.scrollLeft}px)`;
    const l = labelsCol.current;
    if (l && labelsInner.current) {
      // у тела есть горизонтальная полоса прокрутки, у колонки нет: добираем её высоту снизу, иначе у конца колонка отстаёт
      const pad = `${g.offsetHeight - g.clientHeight}px`;
      if (labelsInner.current.style.paddingBottom !== pad) labelsInner.current.style.paddingBottom = pad;
      if (l.scrollTop !== g.scrollTop) { labelsSet.current = g.scrollTop; l.scrollTop = g.scrollTop; }
    }
  }, [gridRef]);

  // Якорь: строка у верхнего края и смещение внутри неё. Когда высоты строк меняются (режим
  // полных названий, чужая правка добавила трек выше) — прокрутка ставится так, чтобы эта строка
  // осталась на месте, а не «всё куда-то уехало».
  const anchor = useRef<{ key: string; offset: number } | null>(null);
  const heightsRef = useRef<string>('');
  const onScroll = useCallback(() => {
    sync();
    const g = gridRef.current;
    if (!g) return;
    let y = 0;
    for (const r of rows) {
      if (g.scrollTop < y + r.height) { anchor.current = { key: r.key, offset: g.scrollTop - y }; return; }
      y += r.height;
    }
    anchor.current = null;
  }, [gridRef, rows, sync]);
  const heights = rows.map((r) => `${r.key}:${r.height}`).join('|');
  useLayoutEffect(() => {
    const changed = heightsRef.current && heightsRef.current !== heights;
    heightsRef.current = heights;
    const g = gridRef.current;
    if (!changed || !g || !anchor.current) return;
    let y = 0;
    for (const r of rows) {
      if (r.key === anchor.current.key) { g.scrollTop = y + anchor.current.offset; sync(); return; }
      y += r.height;
    }
  }, [heights, rows, gridRef, sync]);

  // overflow:hidden не мешает браузеру прокрутить шапку или колонку названий программно
  // (scrollIntoView при фокусе по Tab) — тогда они разъезжаются с телом. Шапку возвращаем на
  // место, перенося её прокрутку в тело; колонку — тело просто догоняет.
  const adoptX = (el: HTMLDivElement) => {
    const g = gridRef.current;
    if (!g) return;
    if (el.scrollLeft) { g.scrollLeft += el.scrollLeft; el.scrollLeft = 0; }
  };
  // Своё эхо пропускаем: иначе во время плавной прокрутки тела (Jump to the task) промежуточное
  // значение вернулось бы в тело и оборвало анимацию.
  const followY = (el: HTMLDivElement) => {
    const g = gridRef.current;
    if (!g || Math.abs(el.scrollTop - labelsSet.current) < 1) return;
    if (g.scrollTop !== el.scrollTop) g.scrollTop = el.scrollTop;
  };

  // Соседние строки одной группы — один блок в обеих колонках: липкая строка держится у верха,
  // пока прокручивается её блок, и уходит вместе с ним.
  const blocks: SplitRow[][] = [];
  for (const r of rows) {
    const last = blocks[blocks.length - 1];
    if (last && r.group !== undefined && last[0]!.group === r.group) last.push(r);
    else blocks.push([r]);
  }
  const stickyClass = (r: SplitRow) => r.sticky && 'sticky top-0 z-[7]';

  // Колесо над шапкой или названиями (overflow: hidden) — прокрутить тело.
  const wheel = (e: ReactWheelEvent) => {
    const g = gridRef.current;
    if (!g) return;
    g.scrollTop += e.deltaY;
    g.scrollLeft += e.deltaX;
  };

  return (
    <div className="flex-1 min-h-0 flex flex-col">
      <div className="flex shrink-0 border-b-2 border-gray-300">
        <GridCorner width={labelWidth} label={cornerLabel} extra={context?.corner} />
        <div className="flex-1 overflow-hidden [scrollbar-gutter:stable]" onScroll={(e) => adoptX(e.currentTarget)} onWheel={wheel}>
          <div ref={headerInner} style={{ width: grid.width }}>
            {header ?? <GridHeader grid={grid} />}
            {context?.node}
          </div>
        </div>
      </div>
      <div className="flex flex-1 min-h-0">
        <div ref={labelsCol} className="shrink-0 overflow-hidden border-r-2 border-gray-300" style={{ width: labelWidth }} onScroll={(e) => followY(e.currentTarget)} onWheel={wheel}>
          <div ref={labelsInner}>
            {blocks.map((b) => (
              <div key={b[0]!.key}>
                {b.map((r) => (
                  <div key={r.key} className={cn('overflow-hidden', r.className, r.labelClassName, stickyClass(r))} style={{ ...r.style, height: r.height }}>{r.label}</div>
                ))}
              </div>
            ))}
          </div>
        </div>
        <div ref={gridRef} data-grid-body className="flex-1 overflow-auto scroller relative [scrollbar-gutter:stable]" style={{ cursor }} onScroll={onScroll}>
          <div className="relative" style={{ width: grid.width }}>
            {blocks.map((b) => (
              <div key={b[0]!.key}>
                {b.map((r) => (
                  <div key={r.key} {...r.gridAttrs} className={cn('relative', r.className, r.gridClassName, stickyClass(r))} style={{ ...r.style, height: r.height, width: grid.width }}>{r.grid}</div>
                ))}
              </div>
            ))}
            {overlay}
          </div>
        </div>
      </div>
    </div>
  );
}
